import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { type TextInput } from "react-native";
import { useTranslation } from "react-i18next";
import {
  AttachmentType,
  DocumentStatus,
  StoragePathType,
  useGetDocumentQuery,
  useProcessCompanionChatAttachmentMutation,
} from "@axio/graphql/generated/graphql";
import useFileUpload, { ProcessedFile } from "../../hooks/useFileUpload";
import { AddTaskModal, Todo } from "../../components/chatNewInterface/interfaceComponents/modals/AddTaskModal";
import {
  AddAssignmentModal,
  Assignment,
} from "../../components/chatNewInterface/interfaceComponents/modals/AddAssignmentModal";
import ChatInput from "./ChatInput";
import type { AttachmentInputType } from "./companionMessage.types";
import {
  TOOL_OPTIONS_BY_CHAT_TYPE,
  type ToolId,
} from "../../components/chatNewInterface/interfaceComponents/ToolsMenu/toolOptions";
import { useMathVirtualKeyboardContext } from "../../contexts/MathVirtualKeyboardContext";
import useChatSpeechInput, { appendTranscriptToDraft } from "../../hooks/useChatSpeechInput";
import { getMathModePreference, saveMathModePreference } from "../../utils/mathModeStorage";
import { type PlaybackSpeed } from "../../utils/toolPreferenceStorage";
import { useChatAudioPreferences } from "../../contexts/ChatAudioPreferencesContext";
import { useAppConfig } from "../../AppConfigProvider";

const VOICE_INPUT_ERROR_TIMEOUT_MS = 3000;
const PROCESSING_TIMEOUT_MS = 120_000;

export type ShowErrorFn = (
  error: {
    title: string;
    message: string;
    onRetry?: () => void;
    retryButtonText?: string;
  } | null
) => void;

interface ChatInputControllerProps {
  onSubmit: (
    message: string,
    attachment: AttachmentInputType | null,
    options?: {
      workspaceIdOverride?: string | null;
      activeTool?: ToolId | null;
    }
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  inputAccessibilityLabel?: string;
  activeWorkspaceType: "general" | "course" | "support";
  showSuggestions?: boolean;
  preselectedAssignment?: Assignment | null;
  preselectedTodo?: Todo | null;
  isEmpty?: boolean;
  showError?: ShowErrorFn;
  onAudioToggleChange?: (enabled: boolean) => void;
  onPlaybackSpeedChange?: (speed: PlaybackSpeed) => void;
  deepResearchStreamActive?: boolean;
}

export default function ChatInputController({
  onSubmit,
  disabled,
  placeholder,
  inputAccessibilityLabel,
  activeWorkspaceType,
  showSuggestions = true,
  preselectedAssignment,
  preselectedTodo,
  showError,
  onAudioToggleChange,
  onPlaybackSpeedChange,
  deepResearchStreamActive = false,
}: ChatInputControllerProps) {
  const { t } = useTranslation();
  const appConfig = useAppConfig();
  const [value, setValue] = useState("");
  const [voiceInputErrorMessage, setVoiceInputErrorMessage] = useState<string | null>(null);
  const [activeToolId, setActiveToolId] = useState<ToolId | null>(null);
  const [isMathModeEnabled, setIsMathModeEnabled] = useState(false);
  const { isAudioOn, playbackSpeed, setIsAudioOn, setPlaybackSpeed } = useChatAudioPreferences();
  const inputRef = useRef<TextInput>(null);
  const vkContext = useMathVirtualKeyboardContext();

  const tools = useMemo(() => {
    const baseTools = TOOL_OPTIONS_BY_CHAT_TYPE.companion;
    return baseTools.filter((tool) => {
      if (tool.id === "deepResearch" && !appConfig?.deepResearchSupportFlag) {
        return false;
      }
      if (tool.id === "createImage" && !appConfig?.imageGenerationSupportFlag) {
        return false;
      }
      return true;
    });
  }, [appConfig?.deepResearchSupportFlag, appConfig?.imageGenerationSupportFlag]);

  useEffect(() => {
    const hiddenActiveTool =
      (activeToolId === "deepResearch" && !appConfig?.deepResearchSupportFlag) ||
      (activeToolId === "createImage" && !appConfig?.imageGenerationSupportFlag);

    if (hiddenActiveTool) {
      setActiveToolId(null);
    }
  }, [activeToolId, appConfig?.deepResearchSupportFlag, appConfig?.imageGenerationSupportFlag]);

  useEffect(() => {
    getMathModePreference().then(setIsMathModeEnabled);
  }, []);

  useEffect(() => {
    onAudioToggleChange?.(isAudioOn);
  }, [isAudioOn, onAudioToggleChange]);

  useEffect(() => {
    onPlaybackSpeedChange?.(playbackSpeed);
  }, [onPlaybackSpeedChange, playbackSpeed]);

  useEffect(() => {
    if (isMathModeEnabled || !vkContext) {
      return;
    }

    vkContext.setVirtualKeyboardVisible(false);
  }, [isMathModeEnabled, vkContext]);

  const [attachment, setAttachment] = useState<AttachmentInputType | null>(null);
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [isAssignmentModalVisible, setIsAssignmentModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Todo | null>(null);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);

  const [processAttachment] = useProcessCompanionChatAttachmentMutation();

  const isDocument = attachment?.type === AttachmentType.Document;
  const documentId = isDocument ? attachment.id : "";

  const {
    data,
    refetch: refetchDocument,
    startPolling,
    stopPolling,
  } = useGetDocumentQuery({
    variables: { id: documentId },
    skip: !isDocument,
    fetchPolicy: "network-only",
  });
  const documentResult = data?.getDocument;
  const documentSuccess = documentResult?.success !== false;

  useEffect(() => {
    if (preselectedAssignment) {
      const { id, title, due_date } = preselectedAssignment;
      setSelectedAssignment(preselectedAssignment);
      setSelectedTask(null);
      setAttachment({
        type: AttachmentType.Assignment,
        id,
        name: title ?? "Untitled Assignment",
        date: due_date ?? undefined,
      });
    }
  }, [preselectedAssignment]);

  useEffect(() => {
    if (preselectedTodo) {
      setSelectedTask(preselectedTodo);
      setSelectedAssignment(null);
      setAttachment({
        type: AttachmentType.Todo,
        id: preselectedTodo.id,
        name: preselectedTodo.name,
        date: preselectedTodo.date ? String(preselectedTodo.date) : undefined,
      });
    }
  }, [preselectedTodo]);

  useEffect(() => {
    if (!voiceInputErrorMessage) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setVoiceInputErrorMessage(null);
    }, VOICE_INPUT_ERROR_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [voiceInputErrorMessage]);

  const removeAttachment = useCallback(() => {
    setAttachment(null);
    setSelectedTask(null);
    setSelectedAssignment(null);
    setIsFileProcessing(false);
    setUploadedFileName("");
    stopPolling?.();
  }, [stopPolling]);

  useEffect(() => {
    if (!isDocument || !isFileProcessing) return;

    startPolling(3000);

    const timeoutId = setTimeout(() => {
      stopPolling();
      removeAttachment();
      showError?.({
        title: "Processing timed out",
        message: "The document took too long to process. Please try again.",
        onRetry: () => showError?.(null),
        retryButtonText: "OK",
      });
    }, PROCESSING_TIMEOUT_MS);

    return () => {
      stopPolling();
      clearTimeout(timeoutId);
    };
  }, [isDocument, isFileProcessing, startPolling, stopPolling, removeAttachment, showError]);

  useEffect(() => {
    if (!isDocument || documentSuccess !== false || !showError) {
      return;
    }

    const message = documentResult?.message ?? "Failed to load document.";
    showError({
      title: "Failed to load document",
      message,
      onRetry: async () => {
        await refetchDocument();
      },
      retryButtonText: "Retry",
    });
  }, [documentResult?.message, documentSuccess, isDocument, refetchDocument, showError]);

  useEffect(() => {
    const doc = documentResult?.data;
    if (!doc) {
      return;
    }

    const firstFileName = doc.files?.[0]?.fileName;
    if (firstFileName) {
      setUploadedFileName(firstFileName);
    }

    if (doc.status === DocumentStatus.Completed) {
      setIsFileProcessing(false);
      stopPolling();
    }

    if (doc.status === DocumentStatus.Failed) {
      console.error("Document processing failed");
      showError?.({
        title: "Document processing failed",
        message: "The document could not be processed. Please try another file.",
        onRetry: async () => {
          removeAttachment();
        },
        retryButtonText: "OK",
      });
      removeAttachment();
    }
  }, [documentResult, removeAttachment, showError, stopPolling]);

  const handleSelectTask = (task: Todo) => {
    setSelectedTask(task);
    setSelectedAssignment(null);
    setAttachment({
      type: AttachmentType.Todo,
      id: task.id,
      name: task.name,
      date: task.date ? String(task.date) : undefined,
    });
    setIsTaskModalVisible(false);
  };

  const handleSelectAssignment = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    setSelectedTask(null);
    setAttachment({
      type: AttachmentType.Assignment,
      id: assignment.id,
      name: assignment.title ?? undefined,
      date: assignment.due_date ?? undefined,
    });
    setIsAssignmentModalVisible(false);
  };

  const { handleFileUpload } = useFileUpload({
    storagePath: StoragePathType.ChatAttachments,
    beforeUpload: () => {
      setIsFileProcessing(true);
      setUploadedFileName("");
    },
    afterUpload: async (files: ProcessedFile[]) => {
      const file = files[0];
      setUploadedFileName(file.file.fileName || "Document");

      try {
        const res = await processAttachment({
          variables: { fileId: file.file.id },
        });

        const result = res.data?.processCompanionChatAttachment;
        const docId = result?.data?.documentId;
        if (!docId || !result?.success) {
          const message = result?.message || "The attachment could not be processed.";
          setIsFileProcessing(false);
          setUploadedFileName("");
          removeAttachment();
          showError?.({
            title: "Failed to process attachment",
            message,
            onRetry: () => showError?.(null),
            retryButtonText: "OK",
          });
          return;
        }

        setAttachment({
          type: AttachmentType.Document,
          id: docId,
          fileId: file.file.id,
          name: file.file.fileName || "Document",
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "An error occurred while processing the attachment.";
        setIsFileProcessing(false);
        setUploadedFileName("");
        removeAttachment();
        showError?.({
          title: "Failed to process attachment",
          message,
          onRetry: () => showError?.(null),
          retryButtonText: "OK",
        });
      }
    },
    onError: (message) => {
      removeAttachment();
      showError?.({
        title: "Upload failed",
        message: message || "An error occurred during upload. Please try again.",
        onRetry: () => showError?.(null),
        retryButtonText: "OK",
      });
    },
    multiple: false,
  });

  const handleAddFileRef = useRef<() => Promise<void>>(async () => {});
  const handleAddFile = useCallback(async () => {
    try {
      const files = await handleFileUpload();

      if (!files || files.length === 0) {
        setIsFileProcessing(false);
        setUploadedFileName("");
      }
    } catch (err) {
      console.error("File upload failed:", err);
      setIsFileProcessing(false);
      setUploadedFileName("");
      removeAttachment();
      const message = err instanceof Error ? err.message : "An error occurred while uploading the file.";
      showError?.({
        title: "Failed to upload file",
        message,
        onRetry: () => {
          showError?.(null);
          void handleAddFileRef.current();
        },
        retryButtonText: "Retry",
      });
    }
  }, [handleFileUpload, removeAttachment, showError]);
  handleAddFileRef.current = handleAddFile;

  const handleTranscriptReady = useCallback((transcript: string) => {
    setVoiceInputErrorMessage(null);
    setValue((currentValue) => appendTranscriptToDraft(currentValue, transcript));
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const handleVoiceInputError = useCallback(() => {
    setVoiceInputErrorMessage(t("Something went wrong with dictation. Try again."));
  }, [t]);

  const handleChangeText = useCallback((text: string) => {
    setVoiceInputErrorMessage(null);
    setValue(text);
  }, []);

  const {
    phase: voiceInputPhase,
    audioLevel,
    handleRecordingCancelPress,
    handleRecordingStartPress,
    handleRecordingStopPress,
  } = useChatSpeechInput({
    isDisabled: Boolean(disabled || isFileProcessing),
    onTranscriptReady: handleTranscriptReady,
    onError: handleVoiceInputError,
  });

  const handleRecordingStart = useCallback(() => {
    setVoiceInputErrorMessage(null);
    handleRecordingStartPress();
  }, [handleRecordingStartPress]);

  const handleRecordingCancel = useCallback(() => {
    setVoiceInputErrorMessage(null);
    handleRecordingCancelPress();
  }, [handleRecordingCancelPress]);

  const attachedTasks = useMemo(
    () =>
      attachment?.type === AttachmentType.Todo && selectedTask
        ? [
            {
              id: selectedTask.id,
              type: "task",
              name: selectedTask.name ?? "",
              duedate: selectedTask.date ?? undefined,
              isLoading: false,
            },
          ]
        : [],
    [attachment, selectedTask]
  );

  const attachedAssignments = useMemo(
    () =>
      attachment?.type === AttachmentType.Assignment && selectedAssignment
        ? [
            {
              id: selectedAssignment.id,
              type: "assignment",
              name: selectedAssignment.title ?? "",
              duedate: selectedAssignment.due_date ?? undefined,
              isLoading: false,
            },
          ]
        : [],
    [attachment, selectedAssignment]
  );

  const attachedFiles = useMemo(() => {
    if (attachment?.type !== AttachmentType.Document) {
      return [];
    }

    const file = documentResult?.data?.files?.[0];
    return [
      {
        id: attachment.id,
        fileName: file?.fileName || uploadedFileName || "Document",
        isLoading: isFileProcessing,
      },
    ];
  }, [attachment, documentResult, isFileProcessing, uploadedFileName]);

  const handleSend = useCallback(
    (message: string) => {
      if (disabled || isFileProcessing || voiceInputPhase !== "idle") {
        return;
      }

      const toSend = message.trim();
      if (!toSend) {
        return;
      }

      const currentActiveTool = activeToolId;
      setVoiceInputErrorMessage(null);
      setActiveToolId(null);
      onSubmit(toSend, attachment, {
        activeTool: currentActiveTool,
      });
      setValue("");
      removeAttachment();
    },
    [activeToolId, attachment, disabled, isFileProcessing, onSubmit, removeAttachment, voiceInputPhase]
  );

  const handleToolSelect = useCallback(
    (toolId: ToolId) => {
      if (deepResearchStreamActive && toolId === "deepResearch") return;
      const nextToolId = activeToolId === toolId ? null : toolId;
      setActiveToolId(nextToolId);
    },
    [activeToolId, deepResearchStreamActive]
  );

  const handleMathModeToggle = useCallback((enabled: boolean) => {
    setIsMathModeEnabled(enabled);
    saveMathModePreference(enabled);
  }, []);

  const handleAudioToggleChange = useCallback(
    (enabled: boolean) => {
      setIsAudioOn(enabled);
      onAudioToggleChange?.(enabled);
    },
    [onAudioToggleChange, setIsAudioOn]
  );

  const handlePlaybackSpeedChange = useCallback(
    (speed: PlaybackSpeed) => {
      setPlaybackSpeed(speed);
      onPlaybackSpeedChange?.(speed);
    },
    [onPlaybackSpeedChange, setPlaybackSpeed]
  );

  return (
    <ChatInput
      value={value}
      voiceInputErrorMessage={voiceInputErrorMessage}
      onChangeText={handleChangeText}
      onSubmit={handleSend}
      inputRef={inputRef}
      disabled={disabled || isFileProcessing}
      placeholder={placeholder}
      inputAccessibilityLabel={inputAccessibilityLabel}
      showSuggestions={showSuggestions}
      activeWorkspaceType={activeWorkspaceType}
      tools={tools}
      activeToolId={activeToolId}
      onToolSelect={handleToolSelect}
      mathModeEnabled={isMathModeEnabled}
      onMathModeToggle={handleMathModeToggle}
      isAudioOn={isAudioOn}
      onAudioToggleChange={handleAudioToggleChange}
      playbackSpeed={playbackSpeed}
      onPlaybackSpeedChange={handlePlaybackSpeedChange}
      attachedTasks={attachedTasks}
      attachedAssignments={attachedAssignments}
      attachedFiles={attachedFiles}
      onAddTask={() => setIsTaskModalVisible(true)}
      onAddAssignment={() => setIsAssignmentModalVisible(true)}
      onAddFile={handleAddFile}
      onRemoveAttachment={removeAttachment}
      voiceInputPhase={voiceInputPhase}
      audioLevel={audioLevel}
      onRecordingStartPress={handleRecordingStart}
      onRecordingCancelPress={handleRecordingCancel}
      onRecordingStopPress={handleRecordingStopPress}
      taskModal={
        <AddTaskModal
          visible={isTaskModalVisible}
          onClose={() => setIsTaskModalVisible(false)}
          onSelectTask={handleSelectTask}
        />
      }
      assignmentModal={
        <AddAssignmentModal
          visible={isAssignmentModalVisible}
          onClose={() => setIsAssignmentModalVisible(false)}
          onSelectAssignment={handleSelectAssignment}
        />
      }
    />
  );
}
